import React, { useState } from "react";
import { GoogleLogin } from "@react-oauth/google";
import { toast } from "react-toastify";
import * as yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import { Button } from "../Button";
import { Input } from "../Input";
import {
  GoogleIcon,
  AppleIcon,
  // FacebookIcon,
  MessageIcon,
} from "../../assets/icons";
import { useDarkMode } from "../../context/DarkModeContext";
import DataUpLogo from "../../assets/images/DataUpLogoAuth.svg";
import { login, loginApple, loginGoogle } from "../../features/store/authSlice";
import { useAppSelector, useAppDispatch } from "../../hooks/hooks";
import {
  LoginFormContainerProps,
  LoginFormProps,
  FooterLinksProps,
  FormData,
} from "./types";
import useClickOutside from "../../hooks/useClickOutside";
import { useSocialLogin } from "../../context/SocialLoginConnect";

const isInReactNativeWebView = () => Boolean(window.ReactNativeWebView);

const requestNativeGoogleLogin = () => {
  window.ReactNativeWebView?.postMessage(JSON.stringify({ action: "google-login" }));
};

const requestNativeAppleLogin = () => {
  window.ReactNativeWebView?.postMessage(JSON.stringify({ action: "apple-login" }));
};

let appleScriptPromise: Promise<void> | null = null;

const loadAppleSignInScript = () => {
  if (window.AppleID?.auth) return Promise.resolve();
  if (appleScriptPromise) return appleScriptPromise;

  appleScriptPromise = new Promise((resolve, reject) => {
    const existingScript = document.querySelector<HTMLScriptElement>(
      'script[src="https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js"]',
    );

    if (existingScript) {
      existingScript.addEventListener("load", () => resolve(), { once: true });
      existingScript.addEventListener("error", () => reject(), { once: true });
      return;
    }

    const script = document.createElement("script");
    script.src =
      "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js";
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject();
    document.head.appendChild(script);
  });

  return appleScriptPromise;
};

const socialButtonClass =
  "p-2 border rounded-md flex items-center justify-center gap-2 min-w-[220px] bg-white text-gray-800 dark:bg-transparent dark:border-gray-700 dark:text-white";

const NativeGoogleLoginButton = () => (
  <button
    type="button"
    onClick={requestNativeGoogleLogin}
    className={socialButtonClass}
  >
    <GoogleIcon />
    <span>Continue with Google</span>
  </button>
);

const AppleLoginButton = ({ onClick }: { onClick: () => void }) => (
  <button type="button" onClick={onClick} className={socialButtonClass}>
    <AppleIcon />
    <span>Continue with Apple</span>
  </button>
);

export const LoginFormContainer: React.FC<LoginFormContainerProps> = ({
  onForgotPassword,
  onSignUp,
  onSubmit,
  error,
  user,
  closeLoginModal, // <-- Ensure this is included
}) => {
  const clickRef = useClickOutside(closeLoginModal);
  const { showGoogleConnectPrompt, setShowGoogleConnectPrompt } =
    useSocialLogin();
  const SocialLoginButtons = () => (
    <div className="flex flex-col gap-3 items-center justify-center mb-4">
      {isInReactNativeWebView() ? (
        <NativeGoogleLoginButton />
      ) : (
        <GoogleLogin
          width="220"
          onSuccess={handleGoogleLogin}
          onError={() => toast.error("Google login failed.")}
        />
      )}
      <AppleLoginButton onClick={handleAppleLogin} />
    </div>
  );

  const dispatch = useAppDispatch();

  const handleGoogleLogin = async (credentialResponse: any) => {
    if (!credentialResponse?.credential) {
      toast.error("Google login failed.");
      return;
    }
    try {
      await dispatch(loginGoogle(credentialResponse.credential)).unwrap();
      if (showGoogleConnectPrompt) {
        toast.success("🎉 Google account linked successfully!");
        setShowGoogleConnectPrompt(false);
      } else {
        toast.success("🎉 Logged in successfully!");
      }
      closeLoginModal();
    } catch (error: any) {
      toast.error(error.message || "Google login failed.");
    }
  };

  const handleAppleLogin = async () => {
    if (isInReactNativeWebView()) {
      requestNativeAppleLogin();
      return;
    }

    const appleClientId = process.env.REACT_APP_APPLE_CLIENT_ID;
    if (!appleClientId) {
      toast.error("Apple login is not configured.");
      return;
    }

    try {
      await loadAppleSignInScript();
      if (!window.AppleID?.auth) {
        toast.error("Apple login is unavailable.");
        return;
      }

      const state = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const nonce = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      window.AppleID.auth.init({
        clientId: appleClientId,
        scope: "name email",
        redirectURI:
          process.env.REACT_APP_APPLE_REDIRECT_URI ||
          `${window.location.origin}/apple-auth`,
        state,
        nonce,
        usePopup: true,
      });

      const result = await window.AppleID.auth.signIn();
      const token = result?.authorization?.id_token;

      if (!token) {
        toast.error("Apple login failed.");
        return;
      }

      await dispatch(
        loginApple({
          token,
          firstname: result?.user?.name?.firstName,
          lastname: result?.user?.name?.lastName,
        }),
      ).unwrap();
      toast.success("🎉 Logged in successfully!");
      closeLoginModal();
    } catch (error: any) {
      toast.error(error.message || "Apple login failed.");
    }
  };

  return (
    <div ref={clickRef}>
      <Logo />
      <Title />
      <div className="space-y-4">
        <SocialLoginButtons />

        <OrDivider />
        <LoginForm
          onSubmit={onSubmit}
          onForgotPassword={onForgotPassword}
          closeLoginModal={closeLoginModal}
        />
        <StatusMessages error={error} user={user} />
        <FooterLinks onForgotPassword={onForgotPassword} onSignUp={onSignUp} />
      </div>
    </div>
  );
};

const Logo = () => (
  <div className="flex justify-center mb-4">
    <img
      src={DataUpLogo}
      alt="DatauplogoAuth"
      className="w-[135.55px] h-[32px]"
    />
  </div>
);

const Title = () => (
  <h2 className="text-2xl font-bold text-center mb-6 dark:text-white">
    Welcome back
  </h2>
);

// const SocialButton = ({ icon }: { icon: React.ReactNode }) => (
//   <button className="p-2 border rounded-md w-32 flex items-center justify-center">
//     {icon}
//   </button>
// );

const OrDivider = () => (
  <div className="flex items-center my-4">
    <div className="flex-1 border-t border-gray-300"></div>
    <div className="px-4 text-gray-500 text-sm dark:text-white">OR</div>
    <div className="flex-1 border-t border-gray-300"></div>
  </div>
);

const LoginForm = ({ onForgotPassword, closeLoginModal }: LoginFormProps) => {
  const schema = yup
    .object({
      identifier: yup.string().required().label("Email"),
      password: yup.string().required().label("Password"),
    })
    .required();

  const { register, handleSubmit, reset } = useForm<FormData>({
    resolver: yupResolver(schema),
    mode: "onSubmit",
    reValidateMode: "onSubmit",
  });

  const dispatch = useAppDispatch();
  const { darkMode } = useDarkMode();
  const { error } = useAppSelector((state) => state.auth);
  const [isLoading, setIsLoading] = useState(false);

  const onFormSubmit = async (data: FormData) => {
    setIsLoading(true);
    await dispatch(login(data))
      .unwrap()
      .then(() => {
        toast.success("🎉 Logged in successfully!");
        reset();
        closeLoginModal();
      })
      .catch(() => {
        toast.error(error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  return (
    <form
      onSubmit={handleSubmit(onFormSubmit)}
      className="space-y-4"
      noValidate
    >
      <Input
        type="text"
        placeholder="Email address/Username"
        icon={<MessageIcon theme={darkMode ? "dark" : "light"} />}
        register={register}
        name="identifier"
        required
      />

      <div className="space-y-2">
        <Input
          type="password"
          placeholder="Password"
          isPassword
          register={register}
          name="password"
          required
        />
        <div className="text-right">
          <button
            type="button"
            onClick={onForgotPassword}
            className="text-xs font-bold dark:text-white hover:underline"
          >
            Forgot Password?
          </button>
        </div>
      </div>

      <Button
        type="submit"
        bgColor="bg-gradient-to-r from-[#027EC2] to-[#039EF2]"
        textColor="text-white"
        borderRadius="rounded-lg"
        className="w-full h-10 flex items-center justify-center gap-2"
        disabled={isLoading}
      >
        {isLoading ? (
          <svg
            className="animate-spin h-5 w-5 text-white"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
            ></path>
          </svg>
        ) : (
          "Sign In"
        )}
      </Button>
    </form>
  );
};

const StatusMessages = ({
  error,
  user,
}: {
  error?: string | null;
  user?: any;
}) => (
  <>
    {error && <p className="text-red-500">{error}</p>}
    {user && <p className="text-green-500">Logged In!</p>}
  </>
);

const FooterLinks = ({ onSignUp }: FooterLinksProps) => (
  <>
    <p className="text-sm md:text-base dark:text-white text-center">
      Don't have an account?{" "}
      <span className="text-[#039EF2] cursor-pointer" onClick={onSignUp}>
        Sign Up
      </span>
    </p>
  </>
);
export default LoginFormContainer;
